import { xmlToJson } from "./xmlUtils";

/**
 * Utiltities for template definitions.
 * @class TemplateUtils
 */
export default class TemplateUtils {
  /**
   * Parses a template definition from XML to JSON.
   * @function parseTemplateDefinition
   * @memberof TemplateUtils
   * @param  {string} rawTemplateDefinitionXml XML version of the template definition.
   * @param  {array} rawTemplateReplaceables
   * @return {string}                           Parsed JSON version of the template definition.
   */
  static parseTemplateDefinition(
    rawTemplateDefinitionXml,
    rawTemplateReplaceables
  ) {
    const rawTemplateDefinitionJson = xmlToJson(rawTemplateDefinitionXml);
    let templateDefinition = {};
    const textReplaceables = [];
    const mediaReplaceables = [];
    let elements = [];
    let customPanel = null;
    const properties = {};
    const channelList = [];
    const layerList = [];
    const primeStyleList = [];
    let primeStyle = "";
    let customMetadata = {};

    if (!rawTemplateDefinitionJson || !rawTemplateDefinitionJson.definition) {
      return null;
    }

    if (
      rawTemplateDefinitionJson.definition.elements &&
      rawTemplateDefinitionJson.definition.elements[0].element &&
      rawTemplateDefinitionJson.definition.elements[0].element.length > 0
    ) {
      elements = rawTemplateDefinitionJson.definition.elements[0].element
        .map(element => {
          const props = {};
          if (element.properties && element.properties.length > 0) {
            for (let i = 0; i < element.properties[0].property.length; i++) {
              const property = element.properties[0].property[i];
              if (property.$.name === "read only") {
                props[property.$.name] = property._ === "Yes";
              } else if (
                property._ &&
                (property._ === "true" || property._ === "false")
              ) {
                props[property.$.name] = property._ === "true";
              } else if (property._ && !isNaN(property._)) {
                props[property.$.name] = parseInt(property._, 10);
              } else {
                props[property.$.name] = property._ || null;
              }
            }
          }

          var value = element.value ? element.value[0] : "";

          let replaceable = {
            id: element.$.id,
            name: element.$.name,
            type: element.$.type,
            value,
            properties: props
          };
          if (element.url) {
            replaceable.url = element.url[0];
          }

          replaceable = updateReplaceableProperties(
            replaceable,
            rawTemplateReplaceables
          );
          return replaceable;
        })
        .sort(
          (first, second) =>
            first.properties["display index"] -
            second.properties["display index"]
        );

      for (let i = 0; i < elements.length; i++) {
        const element = elements[i];
        switch (element.type) {
          case "text":
            textReplaceables.push(element);
            break;
          case "image":
          case "video":
            mediaReplaceables.push(element);
            break;
          default:
            break;
        }
      }
    }

    if (
      rawTemplateDefinitionJson.definition.properties &&
      rawTemplateDefinitionJson.definition.properties[0].property &&
      rawTemplateDefinitionJson.definition.properties[0].property.length > 0
    ) {
      for (
        let i = 0;
        i < rawTemplateDefinitionJson.definition.properties[0].property.length;
        i++
      ) {
        const property =
          rawTemplateDefinitionJson.definition.properties[0].property[i];
        if (property.$.name === "layer") {
          properties[property.$.name] = property._ || "";
        } else {
          properties[property.$.name] = property._ || null;
        }
      }
    }

    if (rawTemplateDefinitionJson.definition.custompanel) {
      customPanel = {
        size: rawTemplateDefinitionJson.definition.custompanel[0].size[0],
        url: rawTemplateDefinitionJson.definition.custompanel[0].url[0]
      };
    }

    if (
      rawTemplateDefinitionJson.definition.virtualchannels &&
      rawTemplateDefinitionJson.definition.virtualchannels[0].virtualchannel &&
      rawTemplateDefinitionJson.definition.virtualchannels[0].virtualchannel
        .length > 0
    ) {
      channelList.push({ id: -1, name: "" });
      for (
        let i = 0;
        i <
        rawTemplateDefinitionJson.definition.virtualchannels[0].virtualchannel
          .length;
        i++
      ) {
        channelList.push(
          rawTemplateDefinitionJson.definition.virtualchannels[0]
            .virtualchannel[i].$
        );
      }
    } else {
      channelList.push({ id: -1, name: "" });
      for (
        let i = 0;
        i <
        rawTemplateDefinitionJson.definition.defaultvirtualchannels[0]
          .defaultvirtualchannel.length;
        i++
      ) {
        if (
          rawTemplateDefinitionJson.definition.defaultvirtualchannels[0]
            .defaultvirtualchannel[i].$.name !== "*"
        ) {
          channelList.push({
            ...rawTemplateDefinitionJson.definition.defaultvirtualchannels[0]
              .defaultvirtualchannel[i].$,
            id: parseInt(
              rawTemplateDefinitionJson.definition.defaultvirtualchannels[0]
                .defaultvirtualchannel[i].$.id,
              10
            )
          });
        }
      }
    }

    if (
      rawTemplateDefinitionJson.definition.layers &&
      rawTemplateDefinitionJson.definition.layers[0].layer &&
      rawTemplateDefinitionJson.definition.layers[0].layer.length > 0
    ) {
      for (
        let i = 0;
        i < rawTemplateDefinitionJson.definition.layers[0].layer.length;
        i++
      ) {
        layerList.push(
          rawTemplateDefinitionJson.definition.layers[0].layer[i].$.layerid
        );
      }
    }

    if (
      rawTemplateDefinitionJson.definition.styles &&
      rawTemplateDefinitionJson.definition.styles[0].style &&
      rawTemplateDefinitionJson.definition.styles[0].style.length > 0
    ) {
      rawTemplateDefinitionJson.definition.styles[0].style.forEach(style => {
        primeStyleList.push({
          id: parseInt(style.$.id, 10),
          name: style._,
          displayIndex: parseInt(style.$.displayIndex, 10)
        });
      });
    }

    if (rawTemplateDefinitionJson.definition.style != null) {
      primeStyle = rawTemplateDefinitionJson.definition.style[0];
    }

    if (rawTemplateDefinitionJson.definition.custommetadata) {
      customMetadata = rawTemplateDefinitionJson.definition.custommetadata[0];
      if (customMetadata) {
        /* eslint-disable */
        for (const prop in customMetadata) {
          if (customMetadata.hasOwnProperty(prop)) {
            customMetadata[prop] = customMetadata[prop][0];
          }
        }
        /* eslint-enable */
      } else {
        customMetadata = {};
      }
    }
    templateDefinition = {
      uuid:
        rawTemplateDefinitionJson.definition.$ &&
        rawTemplateDefinitionJson.definition.$.uuid
          ? rawTemplateDefinitionJson.definition.$.uuid
          : null,
      customPanel,
      textReplaceables,
      mediaReplaceables,
      channelList,
      layerList,
      primeStyleList,
      primeStyle,
      customMetadata,
      properties
    };

    return templateDefinition;
  }
}

const updateReplaceableProperties = (replaceable, rawTemplateReplaceables) => {
  const rawReplaceable = rawTemplateReplaceables.find(
    r => r.id === replaceable.id
  );

  if (!rawReplaceable) {
    return replaceable;
  }

  const props = {};
  rawReplaceable.properties.forEach(p => {
    // validate type and property name
    switch (p.propertyName) {
      case "assetCategory":
      case "autofillDataColumnIndex":
        props[p.propertyName] = parseInt(p.propertyValue, 10);
        break;
      case "dataSource":
        props[p.propertyName] = p.propertyValue;
        if (props.usesDataSource == null) {
          // defaults to true in case value is not passed
          props.usesDataSource = true;
        }
        break;
      case "display index":
      case "displayIndex":
        props["display index"] = parseInt(p.propertyValue, 10);
        break;
      case "maxLength":
        props["max length"] = parseInt(p.propertyValue, 10);
        break;
      case "preview only":
      case "previewOnly":
      case "restrictAssetCategory":
        props[p.propertyName] =
          p.propertyValue === "true" || p.propertyValue === "Yes";
        break;
      case "read only":
      case "readOnly":
        props["read only"] =
          p.propertyValue === "true" || p.propertyValue === "Yes";
        break;
      case "usesDataSource":
        props.usesDataSource =
          p.propertyValue && p.propertyValue.toLowerCase() === "true";
        break;
      default:
        props[p.propertyName] = p.propertyValue ? p.propertyValue : null;
        break;
    }
  });

  if (replaceable.properties["axis template id"]) {
    return Object.assign({}, replaceable, {
      properties: Object.assign({}, props)
    });
  }
  return Object.assign({}, replaceable, {
    properties: Object.assign({}, replaceable.properties, props)
  });
};



// WEBPACK FOOTER //
// ./app/util/templateUtils.js